import CssBaseline from '@mui/material/CssBaseline';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Spinner } from '@ui/core/spinner/Spinner';
import mixpanel from 'mixpanel-browser';
import { SnackbarProvider } from 'notistack';
import { StrictMode, useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { createBrowserRouter, Navigate, Route, RouterProvider, Routes } from 'react-router';
import Maybe from 'true-myth/maybe';

import { AppWithContext } from './AppWithContext';
import { renderErrorCodePage } from './error-handling/ErrorBoundaries';
import { Error404 } from './error-handling/ErrorPages';
import { Layout } from './Layout';
import { lazyRoutesConfig } from './lazy-routes-config';
import { route as dashboardPageRoute } from './pages/dashboard/constants';
import { route as globalSearchRoute } from './pages/global-search-page/constants';
import { GlobalSearchPage } from './pages/global-search-page/GlobalSearch';
import { route as profilePageRoute, userProfileUrl } from './pages/profile/constants';
import { route as talentDirectoryPageRoute } from './pages/talent-directory/constants';
import { TalentDirectoryPage } from './pages/talent-directory/TalentDirectory';
import { PageWithPageViewTracking } from './PageWithPageViewTracking';
import { createRoutesFromRouteDefinitions } from './routes-from-route-definitions';
import { CustomThemeProvider } from './theming';
import { ApplicationContext } from '../../../shared/source/context/context';
import { PageWithTitle } from '../../../shared/source/page-with-title/PageWithTitle';
import { AccessDeniedPage } from '../AccessDeniedPage';
import { createAlignmentRecordPageLoader } from '../alignment-record/store/alignment-record-page-loader';
import { CompanyOnboardingSetupPage } from '../company-onboarding/CompanyOnboardingSetupPage';
import { companyOnboardingRoutePath } from '../company-onboarding/route-config';
import { SkillFeedbackModalRoot } from '../feedback/SkillFeedbackModalRoot';
import { ApplicationFetchersProvider } from '../fetchers/ApplicationFetchersContext';
import { Home } from '../home/Home';
import { RedirectToJobArchitectureV2 } from '../job-architecture-v2/RedirectToJobArchitectureV2';
import { LandingPage } from '../LandingPage';
import { TabProvider } from '../mobile/TabProvider';
import { createMyAlignmentPageLoader } from '../my-alignment/my-alignment-page-loader';
import { createPositionsPageLoader } from '../position/position-page-loader';
import { createFetchAlignmentRecord } from '../position-management/fetch-alignment-record';
import { createPositionManagementPageLoader } from '../position-management/position-management-page-loader';
import { MixpanelTrackingProvider } from '../tracking/mixpanel/MixpanelTrackingProvider';
import { Welcome } from '../welcome/Welcome';

import type { AppDependencies } from './AppWithContext';
import type { UserSettings } from '../../../shared/source/data-contract/user-settings-schema';
import type { AppEnvironment } from '../../../shared/source/environment/environment';
import type { QueryClient } from '@tanstack/react-query';
import type { FunctionComponent } from 'react';

type AppBaseProperties = {
    readonly hasNavigationMenu?: boolean;
    readonly isDataRoute: boolean;
};

const shouldUserCompleteOnboarding = (userSettings: Maybe<UserSettings>): boolean => {
    const startPage = userSettings.get('startPage');

    return startPage.equals(Maybe.just('company-onboarding')) || startPage.equals(Maybe.just('user-onboarding'));
};

const AppBase: FunctionComponent<AppBaseProperties> = (properties) => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const { featureFlagService, myUserProfile, userSettings } = applicationState;

    if (myUserProfile.isNothing) {
        return null;
    }

    if (properties.isDataRoute) {
        return <Layout hasNavigationMenu={properties.hasNavigationMenu} />;
    }

    const isTrial = applicationState.customization.get('tenantMode').equals(Maybe.just('trial'));

    const initialPageForTrialTenant = shouldUserCompleteOnboarding(userSettings) ? (
        <CompanyOnboardingSetupPage />
    ) : (
        <Home />
    );

    return (
        <Routes>
            <Route element={<Layout />}>
                {createRoutesFromRouteDefinitions(
                    { translate },
                    { featureFlagService, routeDefinitions: lazyRoutesConfig },
                )}
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.profile')}>
                            <Navigate replace={true} to={userProfileUrl(myUserProfile.value.id)} />
                        </PageWithTitle>
                    }
                    path={profilePageRoute.patterns.users}
                />
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.profile')}>
                            <Navigate replace={true} to={userProfileUrl(myUserProfile.value.id)} />
                        </PageWithTitle>
                    }
                    path={profilePageRoute.patterns.usersMe}
                />
                <Route
                    element={
                        <PageWithPageViewTracking>
                            <PageWithTitle title={translate('pages.global-search.search-headline')}>
                                <GlobalSearchPage />
                            </PageWithTitle>
                        </PageWithPageViewTracking>
                    }
                    path={globalSearchRoute.patterns.search}
                />
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.dashboard')}>
                            <Navigate replace={true} to={dashboardPageRoute.patterns.dashboard} />
                        </PageWithTitle>
                    }
                    path="/login"
                />
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.dashboard')}>
                            {isTrial ? (
                                initialPageForTrialTenant
                            ) : (
                                <Navigate replace={true} to={dashboardPageRoute.patterns.dashboard} />
                            )}
                        </PageWithTitle>
                    }
                    path="/"
                />
                <Route
                    element={
                        <PageWithTitle title={translate('common.home')}>
                            <Home />
                        </PageWithTitle>
                    }
                    path="/home"
                />
                <Route
                    element={
                        <PageWithTitle title={translate('common.welcome')}>
                            <Welcome />
                        </PageWithTitle>
                    }
                    path="/welcome"
                />
                <Route
                    element={
                        <PageWithTitle title={translate('common.onboarding')}>
                            <CompanyOnboardingSetupPage />
                        </PageWithTitle>
                    }
                    path={companyOnboardingRoutePath}
                />
                <Route
                    element={
                        <PageWithPageViewTracking>
                            <PageWithTitle title={translate('navigation.talent-directory')}>
                                <TalentDirectoryPage />
                            </PageWithTitle>
                        </PageWithPageViewTracking>
                    }
                    path={talentDirectoryPageRoute.patterns.talentDirectory}
                />
                <Route element={<Error404 />} path="*" />
            </Route>
        </Routes>
    );
};

type AppContentProperties = {
    readonly dependencies: AppDependencies;
    readonly hasNavigationMenu?: boolean;
    readonly isDataRoute: boolean;
    readonly shouldShowFeedbackFloatingActionButton?: boolean;
};

const AppContent: FunctionComponent<AppContentProperties> = ({
    dependencies,
    hasNavigationMenu = true,
    isDataRoute,
    shouldShowFeedbackFloatingActionButton = true,
}) => {
    const { authorizationProvider, translate } = dependencies;
    const fallbackDocumentTitle = translate('common.brand');
    const isMobile = false;
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        dependencies.featureFlagService.isInitialized().then((isInitialized) => {
            setIsInitialized(isInitialized);
        });

        // We fall back in case feature flag service is not initialized in time
        setTimeout(() => {
            setIsInitialized(true);
        }, 1000);
    }, [dependencies.featureFlagService]);

    if (authorizationProvider.isBlocked === true) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <AccessDeniedPage />
            </PageWithTitle>
        );
    }

    if (isMobile) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <AppBase hasNavigationMenu={hasNavigationMenu} isDataRoute={isDataRoute} />
            </PageWithTitle>
        );
    }

    if (isInitialized === false) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <div style={{ alignItems: 'center', display: 'flex', height: '100vh', justifyContent: 'center' }}>
                    <Spinner />
                </div>
            </PageWithTitle>
        );
    }

    if (authorizationProvider.isLoggedIn === true) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <SkillFeedbackModalRoot isWithFloatingActionButton={shouldShowFeedbackFloatingActionButton}>
                    <AppBase hasNavigationMenu={hasNavigationMenu} isDataRoute={isDataRoute} />
                </SkillFeedbackModalRoot>
            </PageWithTitle>
        );
    }

    return (
        <PageWithTitle title={fallbackDocumentTitle}>
            <LandingPage />
        </PageWithTitle>
    );
};

type AppProperties = {
    readonly dependencies: AppDependencies;
    readonly environment: AppEnvironment;
    readonly queryClient: QueryClient;
};

type RootProperties = AppProperties & {
    readonly hasNavigationMenu?: boolean;
    readonly isDataRoute: boolean;
    readonly pageTitle: string;
    readonly shouldShowFeedbackFloatingActionButton?: boolean;
};

const Root: FunctionComponent<RootProperties> = (properties) => {
    const {
        dependencies,
        environment,
        hasNavigationMenu,
        isDataRoute,
        pageTitle,
        queryClient,
        shouldShowFeedbackFloatingActionButton,
    } = properties;

    return (
        <StrictMode>
            <QueryClientProvider client={queryClient}>
                <PageWithTitle title={pageTitle}>
                    <ErrorBoundary fallbackRender={renderErrorCodePage(dependencies.translate)}>
                        <AppWithContext dependencies={dependencies} environment={environment}>
                            <MixpanelTrackingProvider client={mixpanel}>
                                <ApplicationFetchersProvider>
                                    <CustomThemeProvider>
                                        <SnackbarProvider maxSnack={3}>
                                            <CssBaseline />
                                            <TabProvider>
                                                <AppContent
                                                    dependencies={dependencies}
                                                    hasNavigationMenu={hasNavigationMenu}
                                                    isDataRoute={isDataRoute}
                                                    shouldShowFeedbackFloatingActionButton={
                                                        shouldShowFeedbackFloatingActionButton
                                                    }
                                                />
                                            </TabProvider>
                                        </SnackbarProvider>
                                    </CustomThemeProvider>
                                </ApplicationFetchersProvider>
                            </MixpanelTrackingProvider>
                        </AppWithContext>
                    </ErrorBoundary>
                </PageWithTitle>
                <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
        </StrictMode>
    );
};

export const App: FunctionComponent<AppProperties> = (properties) => {
    const { dependencies, environment, queryClient } = properties;
    const { translate, validatingCobrainerApiFetcher } = dependencies;

    const router = createBrowserRouter([
        {
            element: <RedirectToJobArchitectureV2 />,
            path: '/job-and-skill-architecture/*',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { JobArchitectureSelectPage } = await import('../job-architecture-v2/select-page/JobArchitectureSelectPage');

                        return {
                            Component: JobArchitectureSelectPage,
                        };
                    },
                },
                {
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                // prettier-ignore
                                const { OverviewTab } = await import('../job-architecture-v2/tabs/overview');

                                return {
                                    Component: OverviewTab,
                                };
                            },
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { OverviewTab } = await import('../job-architecture-v2/tabs/overview');

                                return {
                                    Component: OverviewTab,
                                };
                            },
                            path: 'overview',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { SkillsTab } = await import('../job-architecture-v2/tabs/skills');

                                return {
                                    Component: SkillsTab,
                                };
                            },
                            path: 'skills',
                        },
                    ],
                    lazy: async () => {
                        // prettier-ignore
                        const { JobArchitectureSetupPage } = await import('../job-architecture-v2/JobArchitectureSetupPage');

                        return {
                            Component: JobArchitectureSetupPage,
                        };
                    },
                    path: ':id',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('navigation.job-architecture')}
                    queryClient={queryClient}
                />
            ),
            path: '/job-architecture-v2',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { JobProfileStandaloneSetupPage } = await import('../job-architecture-v2/job-profile-standalone/JobProfileStandaloneSetupPage');

                        return {
                            Component: JobProfileStandaloneSetupPage,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('navigation.job-architecture')}
                    queryClient={queryClient}
                />
            ),
            path: '/job-profile/:architectureId/:itemType/:itemId',
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { PositionManagement } = await import('../position-management/PositionManagement');

                        return {
                            Component: PositionManagement,
                        };
                    },
                    loader: createPositionManagementPageLoader(validatingCobrainerApiFetcher),
                    path: '*',
                },
                {
                    lazy: async () => {
                        const { PositionPage } = await import('../position/PositionPage');

                        return {
                            Component: PositionPage,
                        };
                    },
                    loader: createPositionsPageLoader(validatingCobrainerApiFetcher),
                    path: ':id/:mode?',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('navigation.position-management')}
                    queryClient={queryClient}
                />
            ),
            path: '/position-management/*',
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { AlignmentRecordPage } = await import('../alignment-record/AlignmentRecordPage');

                        return {
                            Component: AlignmentRecordPage,
                        };
                    },
                    loader: createAlignmentRecordPageLoader(createFetchAlignmentRecord(validatingCobrainerApiFetcher)),
                    path: 'users/:userId/records/:id',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('navigation.alignment-record')}
                    queryClient={queryClient}
                    shouldShowFeedbackFloatingActionButton={false}
                />
            ),
            path: '/alignment-record/*',
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { MyAlignmentPage } = await import('../my-alignment/MyAlignmentPage');

                        return {
                            Component: MyAlignmentPage,
                        };
                    },
                    loader: createMyAlignmentPageLoader(validatingCobrainerApiFetcher),
                    path: '*',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('navigation.my-alignment')}
                    queryClient={queryClient}
                    shouldShowFeedbackFloatingActionButton={false}
                />
            ),
            path: '/my-alignment/*',
        },
        {
            children: [
                {
                    children: [
                        {
                            element: <Navigate replace={true} to="organization?sidebar=company-profile" />,
                            index: true,
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { OrganizationTab } = await import('../company-settings/tabs/organization-tab/OrganizationTab');

                                return { Component: OrganizationTab };
                            },
                            path: 'organization',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { JobArchitectureTab } = await import('../company-settings/tabs/job-architecture-tab/JobArchitectureTab');

                                return { Component: JobArchitectureTab };
                            },
                            path: 'job-architecture/:jobArchitectureId?',
                        },
                    ],
                    lazy: async () => {
                        // prettier-ignore
                        const { CompanySettingsRoot } = await import('../company-settings/CompanySettingsRoot');

                        return { Component: CompanySettingsRoot };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('navigation.company-settings')}
                    queryClient={queryClient}
                />
            ),
            path: '/companySettings',
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { StandaloneChatPocPageContent } = await import('../chat-poc/ChatPocPage');

                        return {
                            Component: StandaloneChatPocPageContent,
                        };
                    },
                    path: '*',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle="CHAT"
                    queryClient={queryClient}
                />
            ),
            path: '/chat-poc/*',
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { OnboardingPage } = await import('./pages/onboarding/OnboardingNew');

                        return {
                            Component: OnboardingPage,
                        };
                    },
                    path: '*',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    hasNavigationMenu={false}
                    isDataRoute={true}
                    pageTitle={translate('pages.onboarding.headline')}
                    queryClient={queryClient}
                    shouldShowFeedbackFloatingActionButton={false}
                />
            ),
            path: '/onboarding/*',
        },
        {
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={false}
                    pageTitle={translate('common.brand')}
                    queryClient={queryClient}
                />
            ),
            path: '*',
        },
    ]);

    return <RouterProvider router={router} />;
};
